import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { X, Home, Music, BookOpen, Mic, Palette, ShoppingBag, Building2, Image, ChevronRight, ArrowLeft, Sparkles } from 'lucide-react';

const ChiefNavigationOfficer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const videoRef = useRef(null);
  const navigate = useNavigate();

  // Show tooltip after 4 seconds on page load
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isOpen) {
        setShowTooltip(true);
        // Hide tooltip after 5 seconds
        setTimeout(() => setShowTooltip(false), 5000);
      }
    }, 4000);
    return () => clearTimeout(timer);
  }, [isOpen]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Prevent body scroll when modal is open + auto-play video
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Auto-play video when modal opens
      const timer = setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.currentTime = 0;
          videoRef.current.play().catch(() => {});
        }
      }, 300);
      return () => clearTimeout(timer);
    } else {
      document.body.style.overflow = 'unset';
      if (videoRef.current) {
        videoRef.current.pause();
      }
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const navigationOptions = [
    {
      label: 'Transform my mindset or organization',
      path: '/divisions/mindset-shift-media',
      icon: Music,
      color: 'from-purple-600 to-pink-600'
    },
    {
      label: 'Grow as an author',
      path: '/divisions/author-growth-systems',
      icon: BookOpen,
      color: 'from-green-600 to-emerald-600'
    },
    {
      label: 'Publish or develop a book',
      path: '/divisions/publishing-ghostwriting',
      icon: Mic,
      color: 'from-blue-600 to-cyan-600'
    },
    {
      label: 'Build my brand or marketing assets',
      path: '/divisions/brand-infrastructure',
      icon: Palette,
      color: 'from-orange-600 to-amber-600'
    },
    {
      label: 'Purchase or explore digital assets',
      path: '/store',
      icon: ShoppingBag,
      color: 'from-red-600 to-rose-600'
    },
    {
      label: 'Get listed in the business directory',
      path: '/divisions/business-directory',
      icon: Building2,
      color: 'from-teal-600 to-cyan-600'
    },
    {
      label: 'Explore media & portfolio',
      path: '/portfolio',
      icon: Image,
      color: 'from-indigo-600 to-violet-600'
    },
  ];

  const handleNavigate = (path) => {
    setIsOpen(false);
    navigate(path);
  };

  const handleGoHome = () => {
    setIsOpen(false);
    navigate('/');
  };

  return (
    <>
      {/* Floating "Start Here" Button - Pill Style */}
      <div className="fixed bottom-20 right-6 z-50">
        {/* Tooltip */}
        {showTooltip && !isOpen && (
          <div className="absolute bottom-full right-0 mb-3 bg-white text-gray-900 px-4 py-2 rounded-lg shadow-xl text-sm font-medium whitespace-nowrap animate-bounce">
            Need help navigating? Tap here.
            <div className="absolute -bottom-2 right-8 w-4 h-4 bg-white transform rotate-45"></div>
          </div>
        )}
        
        {/* Button */}
        <button
          onClick={() => {
            setIsOpen(true);
            setShowTooltip(false);
          }}
          className="flex items-center gap-3 bg-red-600 hover:bg-red-700 text-white px-5 py-3 rounded-full shadow-2xl shadow-red-600/40 transition-all duration-300 hover:scale-105 hover:shadow-red-600/60 group"
          aria-label="Open Navigation Assistant"
        >
          <Sparkles className="w-5 h-5 group-hover:animate-pulse" />
          <span className="font-bold text-sm">Start Here</span>
          <span className="text-red-200 text-xs hidden sm:inline">— Tell Us Your Goal</span>
        </button>
      </div>

      {/* Modal Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setIsOpen(false)}
        >
          {/* Modal Content - Fixed scrolling and responsive */}
          <div 
            className="relative bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-2xl my-4 shadow-2xl flex flex-col"
            style={{ maxHeight: '90vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header with Close and Home buttons */}
            <div className="flex items-center justify-between p-4 border-b border-gray-800 flex-shrink-0">
              <button
                onClick={handleGoHome}
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="text-sm font-medium">Back to Home</span>
              </button>
              
              <button
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg"
                aria-label="Close modal"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Video Section - Responsive with Auto-play */}
            <div className="relative bg-black flex-shrink-0">
              <video
                ref={videoRef}
                className="w-full aspect-video object-contain"
                controls
                autoPlay
                muted
                playsInline
                poster="https://customer-assets.emergentagent.com/job_egypt-portfolio/artifacts/ujuixs3p_MINDSET%20SHIFT%20Soundtrack%20Cover.png"
              >
                <source src="https://customer-assets.emergentagent.com/job_eblack-store/artifacts/fefpp5de_CNO_Main_Navigation_Intro.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Content Section - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6 md:p-8">
              {/* Header */}
              <div className="text-center mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-white mb-2">
                  Welcome to eBlackBookListings.
                </h2>
                <p className="text-gray-400 text-sm">
                  Tell us what you're here to accomplish.
                </p>
              </div>

              {/* Navigation Options */}
              <div className="space-y-3">
                {navigationOptions.map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.path}
                      onClick={() => handleNavigate(option.path)}
                      className="w-full flex items-center gap-3 p-3 md:p-4 bg-black/50 border border-gray-800 rounded-xl hover:border-red-600/50 hover:bg-red-600/10 transition-all duration-300 group text-left"
                    >
                      <div className={`w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br ${option.color} rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                        <Icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                      </div>
                      <span className="text-gray-300 group-hover:text-white transition-colors flex-1 text-sm md:text-base">
                        {option.label}
                      </span>
                      <ChevronRight className="w-5 h-5 text-gray-600 group-hover:text-red-500 group-hover:translate-x-1 transition-all flex-shrink-0" />
                    </button>
                  );
                })}
              </div>

              {/* Home Button at Bottom */}
              <div className="mt-6 pt-4 border-t border-gray-800">
                <button
                  onClick={handleGoHome}
                  className="w-full flex items-center justify-center gap-2 p-3 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-colors"
                >
                  <Home className="w-5 h-5" />
                  <span className="font-medium">Return to Homepage</span>
                </button>
              </div>

              {/* Footer */}
              <div className="mt-4 text-center">
                <p className="text-xs text-gray-500">
                  Chief Navigation Officer • Guiding Your Journey
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChiefNavigationOfficer;
